import { useEffect, useState } from 'react';
import axios from 'axios';

import { publicDataDomain } from '../../../util/config';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { usePlayer } from '../../../contexts/PlayerContext';

import { FetchDataHelper } from './fetch-data-helper';
import { MessageHelper } from '../../../util/message-helper';

interface UseFetchInteractProjectDataProps {
	scenarioId: string;
	accountId: string;
	isUsedOnMobile: boolean;
	fromSceneEditor: boolean;
	setLoading?: (_value: boolean) => void;
	updateAndroidInteractOverlayPosition?: () => void;
	getRestrictToken: () => string;
	tokens: string[] | null;
	configParams: any;
}

const useFetchInteractProjectData = ({ scenarioId, accountId, isUsedOnMobile, fromSceneEditor, setLoading, updateAndroidInteractOverlayPosition, getRestrictToken, tokens, configParams }: UseFetchInteractProjectDataProps) => {

	const { setCurrentSceneWidgets } = usePlayer();
	const { scenario, sceneConfig, restrictedToken, restrictedMedias, setCurrentMediaId, setMediasData, setMediaType, setRelatedMediaOfScene, setScenario, setSceneConfig, setRestrictedMedias } = useConfiguration();

	const [interactProjectData, setInteractProjectData] = useState<any>();
	const [processedData, setProcessedData] = useState<any>();

	const getProcessedInteractProjectData = (dataParam: any | undefined, sceneConfigParam: any | undefined, isFirstTime: boolean | undefined) => {
		const data = dataParam ?? scenario;
		if (!data) {
			return;
		}
		const currentSceneId = sceneConfigParam === undefined ? configParams?.sceneId : sceneConfigParam?.currentScene;
		const processedData = FetchDataHelper.getScenesData({
			scenario: data,
			isUsedOnMobile,
			fromSceneEditor,
			isFirstTime: isFirstTime,
			sceneId: currentSceneId ?? sceneConfigParam?.currentScene,
		});

		processedData && setProcessedData(processedData);
		processedData?.currentMediaId && setCurrentMediaId(processedData?.currentMediaId);
		processedData?.sceneConfig && setSceneConfig(processedData?.sceneConfig);
		processedData?.widgetsWithTime && setCurrentSceneWidgets(processedData.widgetsWithTime);

		if (isFirstTime) {
			setInteractProjectData(data);
			data.defaultSceneBGMediaId = processedData?.defaultSceneBGMediaId ?? "";
			setScenario(data);

			processedData?.relatedSceneMedias && setRelatedMediaOfScene(processedData.relatedSceneMedias);
			if (processedData?.mediasToLoad && processedData?.mediasToLoad.length > 0) {
				const mediasData = processedData?.mediasToLoad.filter(Boolean).map((mediaToLoad: any) => FetchDataHelper.getMediaData({
					mediaData: { ...mediaToLoad, id: mediaToLoad.id }, id: mediaToLoad.id, sceneId: mediaToLoad.sceneId
				}))
				setMediasData(mediasData);
			}
		}
	};

	useEffect(() => {
		if (fromSceneEditor) {
			return;
		}
		getProcessedInteractProjectData(undefined, sceneConfig, false);
		updateAndroidInteractOverlayPosition?.()
	}, [sceneConfig?.currentScene, sceneConfig?.nextScene, fromSceneEditor, updateAndroidInteractOverlayPosition]);

	useEffect(() => {
		if (scenario) {
			return;
		}

		// Check tokens to make sure only fetch data when received tokens - avoid Password Screen flashing issue (only when useLoader is true)
		const urlParams = new URLSearchParams(window.location.search);
		const useLoader = urlParams.get('useLoader');

		if (!scenarioId || !accountId || (useLoader === "true" && tokens === null)) {
			return;
		}
		const scenarioUrl = `${publicDataDomain}${accountId}/scenarios/${scenarioId}`;
		axios.get(scenarioUrl, {
			headers: {
				'Content-Type': 'application/json',
				'X-Authorization': 'AVP1 ' + getRestrictToken(),
			}
		}).then((response) => {
			setMediaType('scenario');
			const newRestrictedMedias = restrictedMedias.filter((screen: string) => screen !== "scenario");
			setRestrictedMedias(newRestrictedMedias);

			if (response?.data) {
				getProcessedInteractProjectData(response.data, undefined, true);
			}
			setLoading?.(false);
		}).catch((error) => {
			if (error.response?.data?.token === true) {
				setRestrictedMedias([...restrictedMedias, "scenario"]);
				MessageHelper.sendMessage({
					type: MessageHelper.MESSAGE_TYPES.mediaRestricted,
					mediaId: "",
					scenarioId: scenario?.id,
					metadata: {},
                	qplayerId: configParams?.qplayerId
				});
			}
			setLoading?.(false);
		});
	}, [accountId, scenarioId, setLoading, scenario, restrictedToken, getRestrictToken, tokens, configParams?.qplayerId]);

	return { interactProjectData, ...processedData };
};

export default useFetchInteractProjectData;
